import { PERMISSIONS } from '@/lib/permissions';

/**
 * Catalog of every role-level permission rendered in the Role editor.
 * `bit` is the bitmask flag (shared between client + server), `group`
 * places the option inside a section in the editor UI.
 *
 * Lives outside the main modal file so the Role editor and any future
 * surface that needs to enumerate the available permissions (e.g. a
 * tooltip / search) can import without dragging the modal in.
 */
export const PERMISSION_OPTIONS = [
  { bit: PERMISSIONS.ADMINISTRATOR, labelKey: 'srv2.perm.administrator.label', hintKey: 'srv2.perm.administrator.hint', group: 'general' },
  { bit: PERMISSIONS.MANAGE_GUILD, labelKey: 'srv2.perm.manageGuild.label', hintKey: 'srv2.perm.manageGuild.hint', group: 'general' },
  { bit: PERMISSIONS.MANAGE_ROLES, labelKey: 'srv2.perm.manageRoles.label', hintKey: 'srv2.perm.manageRoles.hint', group: 'general' },
  { bit: PERMISSIONS.MANAGE_CHANNELS, labelKey: 'srv2.perm.manageChannels.label', hintKey: 'srv2.perm.manageChannels.hint', group: 'channels' },
  { bit: PERMISSIONS.VIEW_CHANNEL, labelKey: 'srv2.perm.viewChannels.label', hintKey: 'srv2.perm.viewChannels.hint', group: 'channels' },
  { bit: PERMISSIONS.CREATE_INVITES, labelKey: 'srv2.perm.createInvites.label', hintKey: 'srv2.perm.createInvites.hint', group: 'channels' },
  { bit: PERMISSIONS.SEND_MESSAGES, labelKey: 'srv2.perm.sendMessages.label', hintKey: 'srv2.perm.sendMessages.hint', group: 'messages' },
  { bit: PERMISSIONS.ATTACH_FILES, labelKey: 'srv2.perm.attachFiles.label', hintKey: 'srv2.perm.attachFiles.hint', group: 'messages' },
  { bit: PERMISSIONS.ADD_REACTIONS, labelKey: 'srv2.perm.addReactions.label', hintKey: 'srv2.perm.addReactions.hint', group: 'messages' },
  { bit: PERMISSIONS.EMBED_LINKS, labelKey: 'srv2.perm.embedLinks.label', hintKey: 'srv2.perm.embedLinks.hint', group: 'messages' },
  { bit: PERMISSIONS.READ_MESSAGE_HISTORY, labelKey: 'srv2.perm.readHistory.label', hintKey: 'srv2.perm.readHistory.hint', group: 'messages' },
  { bit: PERMISSIONS.MANAGE_MESSAGES, labelKey: 'srv2.perm.manageMessages.label', hintKey: 'srv2.perm.manageMessages.hint', group: 'messages' },
  { bit: PERMISSIONS.PIN_MESSAGES, labelKey: 'srv2.perm.pinMessages.label', hintKey: 'srv2.perm.pinMessages.hint', group: 'messages' },
  { bit: PERMISSIONS.CONNECT_VOICE, labelKey: 'srv2.perm.connectVoice.label', hintKey: 'srv2.perm.connectVoice.hint', group: 'voice' },
  { bit: PERMISSIONS.SPEAK, labelKey: 'srv2.perm.speak.label', hintKey: 'srv2.perm.speak.hint', group: 'voice' },
  { bit: PERMISSIONS.STREAM_VIDEO, labelKey: 'srv2.perm.streamVideo.label', hintKey: 'srv2.perm.streamVideo.hint', group: 'voice' },
  { bit: PERMISSIONS.KICK_VOICE, labelKey: 'srv2.perm.kickVoice.label', hintKey: 'srv2.perm.kickVoice.hint', group: 'voice' },
  { bit: PERMISSIONS.MUTE_MEMBERS, labelKey: 'srv2.perm.muteMembers.label', hintKey: 'srv2.perm.muteMembers.hint', group: 'voice' },
  { bit: PERMISSIONS.DEAFEN_MEMBERS, labelKey: 'srv2.perm.deafenMembers.label', hintKey: 'srv2.perm.deafenMembers.hint', group: 'voice' },
  { bit: PERMISSIONS.MOVE_MEMBERS, labelKey: 'srv2.perm.moveMembers.label', hintKey: 'srv2.perm.moveMembers.hint', group: 'voice' },
  { bit: PERMISSIONS.BROADCAST_VOICE, labelKey: 'srv2.perm.broadcastVoice.label', hintKey: 'srv2.perm.broadcastVoice.hint', group: 'voice' },
  { bit: PERMISSIONS.KICK_MEMBERS, labelKey: 'srv2.perm.kickMembers.label', hintKey: 'srv2.perm.kickMembers.hint', group: 'moderation' },
  { bit: PERMISSIONS.BAN_MEMBERS, labelKey: 'srv2.perm.banMembers.label', hintKey: 'srv2.perm.banMembers.hint', group: 'moderation' },
  { bit: PERMISSIONS.TIMEOUT_MEMBERS, labelKey: 'srv2.perm.timeoutMembers.label', hintKey: 'srv2.perm.timeoutMembers.hint', group: 'moderation' },
  { bit: PERMISSIONS.VIEW_AUDIT_LOG, labelKey: 'srv2.perm.viewAuditLog.label', hintKey: 'srv2.perm.viewAuditLog.hint', group: 'moderation' },
  { bit: PERMISSIONS.MANAGE_EMOJIS, labelKey: 'srv2.perm.manageEmojis.label', hintKey: 'srv2.perm.manageEmojis.hint', group: 'general' },
] as const;

/** Section headings for `PERMISSION_OPTIONS` grouping. */
export const PERMISSION_GROUPS: { id: string; labelKey: string }[] = [
  { id: 'general', labelKey: 'srv2.permGroup.general' },
  { id: 'channels', labelKey: 'srv2.permGroup.channels' },
  { id: 'messages', labelKey: 'srv2.permGroup.messages' },
  { id: 'voice', labelKey: 'srv2.permGroup.voice' },
  { id: 'moderation', labelKey: 'srv2.permGroup.moderation' },
];

/**
 * Rows of the channel-permissions screen, in display order. Which of them a
 * given channel shows comes from `channelOverridePermissions` (shared with
 * the server's validation): chat rows for text channels, voice rows for
 * voice channels, «в эфир» only for tournament rooms.
 */
export const CHANNEL_OVERRIDE_OPTIONS = [
  { bit: PERMISSIONS.VIEW_CHANNEL, labelKey: 'srv2.override.viewChannel' },
  { bit: PERMISSIONS.SEND_MESSAGES, labelKey: 'srv2.override.sendMessages' },
  { bit: PERMISSIONS.ATTACH_FILES, labelKey: 'srv2.override.attachFiles' },
  { bit: PERMISSIONS.ADD_REACTIONS, labelKey: 'srv2.override.addReactions' },
  { bit: PERMISSIONS.EMBED_LINKS, labelKey: 'srv2.override.embedLinks' },
  { bit: PERMISSIONS.READ_MESSAGE_HISTORY, labelKey: 'srv2.override.readHistory' },
  { bit: PERMISSIONS.CONNECT_VOICE, labelKey: 'srv2.override.connectVoice' },
  { bit: PERMISSIONS.SPEAK, labelKey: 'srv2.override.speak' },
  { bit: PERMISSIONS.STREAM_VIDEO, labelKey: 'srv2.override.streamVideo' },
  { bit: PERMISSIONS.KICK_VOICE, labelKey: 'srv2.override.kickVoice' },
  { bit: PERMISSIONS.MUTE_MEMBERS, labelKey: 'srv2.override.muteMembers' },
  { bit: PERMISSIONS.DEAFEN_MEMBERS, labelKey: 'srv2.override.deafenMembers' },
  { bit: PERMISSIONS.MOVE_MEMBERS, labelKey: 'srv2.override.moveMembers' },
  { bit: PERMISSIONS.BROADCAST_VOICE, labelKey: 'srv2.override.broadcastVoice' },
] as const;

/**
 * Owner / Admin bypass bit-test. Re-exported from the shared permission
 * module (via `@/lib/permissions`) so client + server share one
 * implementation — kept exported here for the editor surfaces that already
 * import it alongside {@link PERMISSION_OPTIONS}.
 */
export { hasPermission } from '@/lib/permissions';
